import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import Error from '@material-ui/icons/Error';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { FieldValidation } from '@rjsf/utils';
import { ApiHolder } from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';

// Validation function for form-level validation (blocks navigation)
export const validateProjectName = async (
  value: string,
  validation: FieldValidation,
  context: { apiHolder: ApiHolder },
) => {
  const currentValue = value || '';
  
  if (!currentValue) return; // Let required validation handle empty values

  try {
    const catalogApi = context.apiHolder.get(catalogApiRef);
    if (!catalogApi) {
      validation.addError('Unable to validate project name. Please try again.');
      return;
    }
    
    // Check if the project exists in the catalog. Proof-of-concept projects carry
    // 'poc' rather than 'project' so they drop out of the catalog projects list,
    // but they are still valid parents for a new component.
    const entities = await catalogApi.getEntities({
      filter: {
        kind: 'Component',
        'spec.type': ['project', 'poc'],
        'metadata.name': currentValue,
      },
    });

    if (entities.items.length === 0) {
      // validation.addError(`Project "${currentValue}" not found. Please create the project first before creating this component.`);
      validation.addError(renderErrorMessage(`Project "${currentValue}" not found. Please create the project first before creating this component.`, currentValue) as string);
      return;
    }

    // Check if there's an exact match (case-insensitive)
    const exactMatch = entities.items.some((project: any) => 
      project.metadata.name.toLowerCase() === currentValue.toLowerCase() ||
      project.metadata.title?.toLowerCase() === currentValue.toLowerCase() ||
      project.spec?.displayName?.toLowerCase() === currentValue.toLowerCase()
    );

    if (!exactMatch) {
      validation.addError(`Please select a valid project from the dropdown. "${currentValue}" is not a valid project.`);
      return;
    }

  } catch (error) {
    validation.addError('Unable to validate project name. Please try again.');
  }
};

interface ProjectEntity {
  metadata: {
    name: string;
    title?: string;
  };
  spec?: {
    displayName?: string;
  };
}

interface ProjectOption {
  value: string;
  label: string;
}

// Helper function to render error message with hyperlink
const renderErrorMessage = (message: any, projectName: string) => {
  // If message is already JSX (from form validation), return it as is
  if (typeof message !== 'string') {
    return message;
  }
  
  if (message.includes('not found') && message.includes('create the project')) {
    // Extract the project name from the message if it's different from inputValue
    const match = message.match(/Project "([^"]+)"/);
    const extractedProjectName = match ? match[1] : projectName;
    
    return (
      <span style={{ color: '#d32f2f' }}>
        Project "{extractedProjectName}" not found. Please{' '}
        <Link 
          to="/requests/templates/default/create-project"
          style={{
            color: '#0066cc',
            textDecoration: 'underline',
            fontWeight: '600',
            fontStyle: 'italic',
            cursor: 'pointer'
          }}
        >
          create the project
        </Link>{' '}
        first before creating this component.
      </span>
    );
  }
  return message;
};

export const CustomProjectNameField = ({
  onChange,
  required,
  formData,
  schema,
  rawErrors,
}: FieldExtensionComponentProps<string>) => {
  const [validationState, setValidationState] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [validationError, setValidationError] = useState<string>('');
  const [availableProjects, setAvailableProjects] = useState<ProjectEntity[]>([]);
  const [inputValue, setInputValue] = useState(formData || '');
  const [isOpen, setIsOpen] = useState(false);
  const catalogApi = useApi(catalogApiRef);

  // Extract schema properties
  const {
    title,
    description,
    placeholder,
    helperText,
  } = schema;

  // Fetch available projects from catalog, including proof-of-concept projects so
  // they remain selectable as a parent even though they are hidden from the
  // catalog projects list.
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const entities = await catalogApi.getEntities({
          filter: {
            kind: 'Component',
            'spec.type': ['project', 'poc'],
          },
        });
        setAvailableProjects(entities.items as ProjectEntity[]);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };

    fetchProjects();
  }, [catalogApi]);

  // Clear validation state when input changes
  useEffect(() => {
    if (formData !== inputValue) {
      setValidationState('idle');
      setValidationError('');
    }
  }, [formData, inputValue]);

  // Create project options
  const projectOptions: ProjectOption[] = useMemo(() => 
    availableProjects.map(project => ({
      value: project.metadata.name,
      label: project.spec?.displayName || project.metadata.title || project.metadata.name,
    })), [availableProjects]
  );

  // Filter options based on input value
  const filteredOptions = useMemo(() => {
    if (!inputValue || inputValue.trim().length === 0) {
      return projectOptions;
    }
    
    const searchTerm = inputValue.toLowerCase();
    return projectOptions.filter(option => 
      option.label.toLowerCase().includes(searchTerm) ||
      option.value.toLowerCase().includes(searchTerm)
    );
  }, [projectOptions, inputValue]);

  // Check if the current input value exactly matches a selected project
  const isExactMatch = useMemo(() => {
    if (!inputValue || inputValue.trim().length === 0) {
      return false;
    }
    
    return projectOptions.some(option => 
      option.value.toLowerCase() === inputValue.toLowerCase() ||
      option.label.toLowerCase() === inputValue.toLowerCase()
    );
  }, [projectOptions, inputValue]);

  // Check for form validation errors (these block navigation)
  const hasFormValidationErrors = rawErrors && rawErrors.length > 0;

  // Only show field-level errors when there are no form validation errors
  const hasError = hasFormValidationErrors || (!hasFormValidationErrors && validationState === 'invalid');
  const displayError = hasFormValidationErrors ? '' : (validationError ? renderErrorMessage(validationError, inputValue) : (helperText || description));

  // Real-time validation for UI feedback
  useEffect(() => {
    if (inputValue && inputValue.trim().length > 0) {
      if (isExactMatch) {
        setValidationState('valid');
        setValidationError('');
      } else {
        setValidationState('invalid');
        setValidationError(`Project "${inputValue}" not found. Please create the project first before creating this component.`);
      }
    } else {
      setValidationState('idle');
      setValidationError('');
    }
  }, [inputValue, isExactMatch]);

  const getEndAdornment = () => {
    // If form validation errors exist, don't show any field-level icons
    if (hasFormValidationErrors) {
      return <div />;
    }
    
    switch (validationState) {
      case 'validating':
        return <CircularProgress size={20} />;
      case 'valid':
        return <div />; // No icon for valid state
      case 'invalid':
        return <Error style={{ color: 'red' }} />;
      default:
        return <div />;
    }
  };

  return (
    <div>
      <Autocomplete
        options={filteredOptions}
        value={projectOptions.find(option => option.value === formData) || null}
        inputValue={inputValue}
        open={isOpen && (inputValue.length === 0 || filteredOptions.length > 0)}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        onInputChange={(_, newInputValue: string) => {
          setInputValue(newInputValue);
          onChange(newInputValue);
        }}
        onChange={(_, newValue: ProjectOption | null) => {
          if (newValue) {
            setInputValue(newValue.value);
            onChange(newValue.value);
            setIsOpen(false); // Close dropdown after selection
          }
        }}
        renderInput={(params: any) => (
          <TextField
            {...params}
            fullWidth
            label={title}
            error={hasError}
            placeholder={placeholder}
            required={required}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  {getEndAdornment()}
                </InputAdornment>
              ),
            }}
          />
        )}
        getOptionLabel={(option: ProjectOption | string) => {
          if (typeof option === 'string') {
            return option;
          }
          return option.label;
        }}
        isOptionEqualToValue={(option: ProjectOption, value: ProjectOption) => 
          option.value === value.value
        }
        noOptionsText=""
        filterOptions={(options) => options}
        freeSolo={false}
        selectOnFocus
        clearOnBlur={false}
        autoComplete
        autoHighlight
      />
      
      {/* Show error message or helper text below the field */}
      {displayError && (
        <div style={{
          color: hasError ? '#d32f2f' : '#666666',
          fontSize: '0.75rem',
          marginTop: '3px',
          marginLeft: '14px',
          marginBottom: '0px'
        }}>
          {displayError}
        </div>
      )}
    </div>
  );
};